import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Reveal, SectionHeading } from "@/components/Primitives";
import Hero3D from "@/components/Hero3D";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import EstimateCalculator from "@/components/EstimateCalculator";
import IdeaFinder from "@/components/IdeaFinder";
import ReferEarnSection from "@/components/ReferEarnSection";
import api from "@/lib/api";
import {
  Smartphone, MessageCircle, Settings2, Rocket, ArrowRight, Store, Search,
  ShieldCheck, Clock, Check, Sparkles,
} from "lucide-react";

const BENEFITS = [
  { icon: Smartphone, t: "Mobile Friendly" },
  { icon: MessageCircle, t: "WhatsApp Enquiries" },
  { icon: Settings2, t: "Easy Content Management" },
  { icon: Rocket, t: "Launch Support" },
];

const PROBLEMS = [
  { icon: Store, t: "Present products professionally", d: "Show your products and services in a polished, trustworthy way." },
  { icon: Search, t: "Easy-to-find information", d: "Hours, location and contact details customers can find in seconds." },
  { icon: MessageCircle, t: "Enquiries that convert", d: "WhatsApp, phone and forms so customers can reach you instantly." },
  { icon: ShieldCheck, t: "Build authentic trust", d: "Real business information that reassures first-time visitors." },
  { icon: Clock, t: "Open after shop hours", d: "Your catalogue stays accessible even when the shop is closed." },
];

export default function Home() {
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState([]);
  useEffect(() => { api.get("/public/portfolio").then((r) => setPortfolio(r.data.slice(0, 3))).catch(() => {}); }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 nova-grid opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-medium text-cyan-200 mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Premium Websites. Powerful First Impressions.
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
              Your Business Deserves a Website That <span className="text-gradient">Wins Trust.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
              We create fast, premium websites that help customers discover your business, explore your services and contact you easily.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-3 mt-8">
              <Button data-testid="hero-free-demo-cta" size="lg" className="bg-violet-600 hover:bg-violet-500 text-white font-semibold btn-glow h-12 px-7" onClick={() => navigate("/contact")}>
                Request Your Free Demo <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button data-testid="hero-explore-work-cta" size="lg" variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/10 h-12 px-7" onClick={() => navigate("/portfolio")}>
                Explore Our Work
              </Button>
            </motion.div>
            <div className="flex flex-wrap gap-x-6 gap-y-3 mt-10">
              {BENEFITS.map((b, i) => (
                <motion.div key={b.t} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 + i * 0.1 }}
                  className="flex items-center gap-2 text-sm text-slate-300">
                  <span className="grid place-items-center w-7 h-7 rounded-lg bg-white/5 text-cyan-300"><b.icon className="w-4 h-4" /></span>{b.t}
                </motion.div>
              ))}
            </div>
          </div>
          <div className="relative"><Hero3D /></div>
        </div>
      </section>

      {/* PROBLEMS / SOLUTIONS */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Why it matters" title="Practical benefits for local businesses"
            subtitle="A premium website does the quiet work of presenting, informing and converting — honestly, without inflated promises." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROBLEMS.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.06}>
                <div className="glass rounded-2xl p-7 card-hover h-full">
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/30 to-cyan-500/20 text-violet-200 mb-5"><p.icon className="w-6 h-6" /></span>
                  <h3 className="font-semibold text-white text-lg mb-2">{p.t}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="py-24 bg-[#0A0E1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Transformation" title="From outdated to outstanding" subtitle="See the difference a premium redesign makes. Drag the handle to compare." />
          <BeforeAfterSlider />
        </div>
      </section>

      {/* PORTFOLIO PREVIEW */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Our work" title="Concept demos across industries" subtitle="Sample projects that show what's possible. All clearly labelled as concept demos." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {portfolio.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <div className="glass rounded-2xl overflow-hidden card-hover">
                  <div className="relative"><img src={p.image} alt={p.title} className="w-full h-48 object-cover" loading="lazy" />
                    <span className="absolute top-3 left-3 text-[10px] font-semibold bg-amber-500/90 text-[#3a2800] px-2 py-1 rounded-full">Concept Demo</span></div>
                  <div className="p-5">
                    <div className="text-xs font-mono text-cyan-300 mb-1">{p.category}</div>
                    <h3 className="font-semibold text-white">{p.title}</h3>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-10"><Link to="/portfolio"><Button variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/10">View full portfolio <ArrowRight className="w-4 h-4 ml-2" /></Button></Link></div>
        </div>
      </section>

      {/* ESTIMATE + IDEA FINDER */}
      <section className="py-24 bg-[#0A0E1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          <EstimateCalculator />
          <IdeaFinder />
        </div>
      </section>

      <ReferEarnSection />

      {/* FINAL CTA */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-strong rounded-3xl p-10 sm:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 nova-grid opacity-20" />
            <div className="relative">
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Ready for a website that wins trust?</h2>
              <p className="mt-4 text-slate-400 max-w-xl mx-auto">Request a free demo and see your business presented the way it deserves.</p>
              <Button data-testid="final-cta-demo" size="lg" className="mt-8 bg-violet-600 hover:bg-violet-500 text-white font-semibold btn-glow h-12 px-8" onClick={() => navigate("/contact")}>
                Request Your Free Demo <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
